import React, { useState, useEffect } from 'react';
import { Button, Input, Checkbox, message, Modal } from 'antd';
import { DownloadOutlined, SendOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { useAtom } from 'jotai';
import { userAtom } from '../../store/atoms';
import { useSecureService } from '../../store/service';
import CompactDrawingToolbox from '../DrawingToolbox/CompactDrawingToolbox';
import { Shape, DrawingTool } from '../VideoPlayer/VideoPlayer';
import VersionManager from '../VersionManager/VersionManager';
import GuestCommentModal from '../../modals/guestCommentModal';
import * as XLSX from 'xlsx';
import styles from '../../pages/projects/Projects.module.scss';

// Comment type definition
export type CommentType = {
  id: number;
  userId: number;
  versionId: number;
  startFrame: number;
  endFrame: number;
  text: string;
  data: string;
  createdAt: string;
  updatedAt?: string | null;
  isResolved?: boolean;
  shapes?: Shape[];
  versionName?: string;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    title: string;
    role: string;
    organizationName?: string | null;
    isActive: boolean;
    createdAt: string;
    lastLoginAt?: string | null;
    thumbnailUrl?: string;
  };
};

interface CommentSectionProps {
  fileDetail: any;
  versions: any[];
  versionsLoading: boolean;
  selectedVersion: number;
  selectedVersionCompare?: number | null;
  selectedCommentId?: number | null;
  showAllComments: boolean;
  onShowAllCommentsChange: (show: boolean) => void;
  onVersionChange: (version: string) => void;
  canComment: boolean;
  canShowAllComments: boolean;
  canResolve: boolean;
  userRole?: string;
  onAddNewFile: () => void;
  
  // Video player states
  compareMode: boolean;
  activePlayer: number;
  currentFrameRange: string;
  currentFrameRangeCompare: string;
  
  // Drawing states
  shapes: Shape[];
  shapesCompare: Shape[];
  drawingTool: DrawingTool;
  drawingColor: string;
  selectedShapeId: string | null;
  selectedShapeIdCompare: string | null;
  onShapesChange: (shapes: Shape[]) => void;
  onShapesCompareChange: (shapes: Shape[]) => void;
  onToolChange: (tool: DrawingTool) => void;
  onColorChange: (color: string) => void;
  onClearShapes: () => void;
  onSave: () => void;
  onDeleteSelected: () => void;
  onDeleteSelectedCompare: () => void;
  onChangeSelectedColor: (color: string) => void;
  onChangeSelectedColorCompare: (color: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onUndoCompare: () => void;
  onRedoCompare: () => void;
  canUndo: boolean;
  canRedo: boolean;
  canUndoCompare: boolean;
  canRedoCompare: boolean;
  
  // Video time states
  currentVideoTime: number;
  currentFps: number;
  videoDuration: number;
  
  // Frame selection states
  selectedFrame: number | null;
  selectedFrameRange: [number, number] | null;
  selectedFrameCompare: number | null;
  selectedFrameRangeCompare: [number, number] | null;
  
  // Comment handlers
  onCommentSelect: (comment: CommentType) => void;
  onCommentSelectCompare: (comment: CommentType) => void;
  onGuestCommentSubmit: (commentData: any, guestData: any) => void;
  onFileDetailRefresh: () => void;
  
  // Close drawing popover
  closeDrawingPopover: boolean;
  onCloseDrawingPopover: (close: boolean) => void;
  isDrawingToolboxOpen: boolean;
  onDrawingToolboxOpenChange: (open: boolean) => void;
  
  // Reset frame selection
  onResetFrameSelection: () => void;
}

const CommentSection: React.FC<CommentSectionProps> = ({
  fileDetail,
  versions,
  versionsLoading,
  selectedVersion,
  selectedVersionCompare,
  selectedCommentId,
  showAllComments,
  onShowAllCommentsChange,
  onVersionChange,
  canComment,
  canShowAllComments,
  canResolve,
  userRole,
  onAddNewFile,
  compareMode,
  activePlayer,
  currentFrameRange,
  currentFrameRangeCompare,
  shapes,
  shapesCompare,
  drawingTool,
  drawingColor,
  selectedShapeId,
  selectedShapeIdCompare,
  onShapesChange,
  onShapesCompareChange,
  onToolChange,
  onColorChange,
  onClearShapes,
  onSave,
  onDeleteSelected,
  onDeleteSelectedCompare,
  onChangeSelectedColor,
  onChangeSelectedColorCompare,
  onUndo,
  onRedo,
  onUndoCompare,
  onRedoCompare,
  canUndo,
  canRedo,
  canUndoCompare,
  canRedoCompare,
  currentVideoTime,
  currentFps,
  videoDuration,
  selectedFrame,
  selectedFrameRange,
  selectedFrameCompare,
  selectedFrameRangeCompare,
  onCommentSelect,
  onCommentSelectCompare,
  onGuestCommentSubmit,
  onFileDetailRefresh,
  closeDrawingPopover,
  onCloseDrawingPopover,
  isDrawingToolboxOpen,
  onDrawingToolboxOpenChange,
  onResetFrameSelection
}) => {
  const { user } = useAuth();
  const [, setUser] = useAtom(userAtom);
  const { createFileComment, createGuestFileComment, resolveFileComment, editFileComment, deleteFileComment, getFileById } = useSecureService();
  
  // Comment input state
  const [commentListInput, setCommentListInput] = useState('');
  
  // Show resolved comments state
  const [showResolved, setShowResolved] = useState(false);

  // showResolved'i localStorage ile senkronize et
  useEffect(() => {
    try {
      const saved = localStorage.getItem('showResolvedComments');
      if (saved === 'true' || saved === 'false') {
        setShowResolved(saved === 'true');
        // İlk yüklemede event yayınla
        window.dispatchEvent(new CustomEvent('showResolvedChanged', { detail: saved === 'true' }));
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('showResolvedComments', showResolved ? 'true' : 'false');
    } catch {}
    // Değişimde event yayınla
    try {
      window.dispatchEvent(new CustomEvent('showResolvedChanged', { detail: showResolved }));
    } catch {}
  }, [showResolved]);
  
  // Mention states
  const [isMentionDropdownVisibleList, setIsMentionDropdownVisibleList] = useState(false);
  const [mentionSearchTermList, setMentionSearchTermList] = useState('');
  const [isMentionDisabledList, setIsMentionDisabledList] = useState(false);
  const [mentionMapping, setMentionMapping] = useState<{[key: string]: string}>({});
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  
  // Guest comment modal states
  const [isGuestCommentModalOpen, setIsGuestCommentModalOpen] = useState(false);
  const [pendingCommentData, setPendingCommentData] = useState<any>(null);
  const [guestCommentLoading, setGuestCommentLoading] = useState(false);
  
  // Resolve loading states
  const [resolvingComments, setResolvingComments] = useState<{[key: number]: boolean}>({});

  const FPS = 30;
  // Edit comment states
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [editCommentLoading, setEditCommentLoading] = useState(false);
  
  // Delete comment states
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  const [deleteCommentLoading, setDeleteCommentLoading] = useState(false);

  // Edit modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editTargetCommentId, setEditTargetCommentId] = useState<number | null>(null);

  // Delete modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTargetCommentId, setDeleteTargetCommentId] = useState<number | null>(null);

  // Check if current user can edit/delete comment
  const canEditDeleteComment = (comment: CommentType) => {
    if (!user || user.role === 'Guest') return false;
    return user.email === comment.user?.email;
  };

  // Handle edit comment
  const handleEditComment = (comment: CommentType) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.text || '');
    setEditTargetCommentId(comment.id);
    setIsEditModalOpen(true);
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    if (!editTargetCommentId || !editCommentText.trim()) return;
    
    setEditCommentLoading(true);
    try {
      const response = await editFileComment(editTargetCommentId, {
        text: editCommentText.trim(),
        data: ''
      });
      
      if (response.success) {
        setIsEditModalOpen(false);
        setEditingCommentId(null);
        setEditTargetCommentId(null);
        setEditCommentText('');
        onFileDetailRefresh();
        message.success('Comment updated successfully');
      } else {
        message.error(response.message || 'Failed to update comment');
      }
    } catch (error) {
      message.error('An error occurred while updating the comment');
    } finally {
      setEditCommentLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditingCommentId(null);
    setEditTargetCommentId(null);
    setEditCommentText('');
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId: number) => {
    setIsDeleteModalOpen(true);
    setDeleteTargetCommentId(commentId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetCommentId) return;
    setDeleteCommentLoading(true);
    try {
      const response = await deleteFileComment(deleteTargetCommentId);
      if (response.success) {
        onFileDetailRefresh();
        message.success('Comment deleted successfully');
        setIsDeleteModalOpen(false);
        setDeleteTargetCommentId(null);
      } else {
        message.error(response.message || 'Failed to delete comment');
      }
    } catch (error) {
      message.error('An error occurred while deleting the comment');
    } finally {
      setDeleteCommentLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeleteTargetCommentId(null);
  };

  // Scroll to selected comment when selectedCommentId changes
  useEffect(() => {
    if (selectedCommentId) {
      // DOM'un render edilmesini bekle
      setTimeout(() => {
        const commentElement = document.getElementById(`comment-${activePlayer}-${selectedCommentId}`);
        if (commentElement) {
          commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [selectedCommentId, activePlayer]);

  // Get accessible versions for guest users
  const getAccessibleVersions = () => {
    if (!user || user.role !== 'Guest') {
      return fileDetail?.versions || [];
    }
    return fileDetail?.versions || [];
  };

  const accessibleVersions = getAccessibleVersions();

  // Get accessible comments for guest users
  const getAccessibleComments = (comments: CommentType[]) => {
    if (!user || user.role !== 'Guest') {
      // Filter resolved comments based on showResolved state
      if (!showResolved) {
        return comments.filter(comment => !comment.isResolved);
      }
      return comments;
    }
    // Filter resolved comments based on showResolved state
    if (!showResolved) {
      return comments.filter(comment => !comment.isResolved);
    }
    return comments;
  };

  // Get all comments based on showAllComments flag
  const getAllComments = () => {
    if (!fileDetail?.versions) return {};
    
    let allComments: CommentType[] = [];
    
    if (showAllComments) {
      // Compare modunda ise sadece compare edilen versiyonların yorumlarını göster
      if (compareMode) {
        // Compare edilen iki versiyonun yorumlarını göster (activePlayer'dan bağımsız)
        const compareVersions = [selectedVersion, selectedVersionCompare].filter(Boolean);
        
        fileDetail.versions.forEach((version: any) => {
          if (compareVersions.includes(version.id) && version.comments && Array.isArray(version.comments)) {
            const versionComments = version.comments.map((comment: any) => ({
              ...comment,
              versionId: version.id,
              versionName: version.name || `v${version.id}`
            }));
            allComments = [...allComments, ...versionComments];
          }
        });
      } else {
        // Normal modda tüm versiyonların yorumlarını göster
        fileDetail.versions.forEach((version: any) => {
          if (version.comments && Array.isArray(version.comments)) {
            const versionComments = version.comments.map((comment: any) => ({
              ...comment,
              versionId: version.id,
              versionName: version.name || `v${version.id}`
            }));
            allComments = [...allComments, ...versionComments];
          }
        });
      }
    } else {
      // showAllComments false ise aktif player'daki versiyonun yorumlarını göster
      if (compareMode) {
        // Compare modunda aktif player'a göre versiyon seç
        let targetVersion: number;
        if (activePlayer === 1) {
          targetVersion = selectedVersion;
        } else {
          targetVersion = selectedVersionCompare || selectedVersion;
        }
        
        const selectedVersionData = fileDetail.versions.find((v: any) => v.id === targetVersion);
        if (selectedVersionData?.comments && Array.isArray(selectedVersionData.comments)) {
          allComments = selectedVersionData.comments.map((comment: any) => ({
            ...comment,
            versionId: selectedVersionData.id,
            versionName: selectedVersionData.name || `v${selectedVersionData.id}`
          }));
        }
      } else {
        // Normal modda seçili versiyonun yorumlarını göster
        const selectedVersionData = fileDetail.versions.find((v: any) => v.id === selectedVersion);
        if (selectedVersionData?.comments && Array.isArray(selectedVersionData.comments)) {
          allComments = selectedVersionData.comments.map((comment: any) => ({
            ...comment,
            versionId: selectedVersionData.id,
            versionName: selectedVersionData.name || `v${selectedVersionData.id}`
          }));
        }
      }
    }
    
    // Apply access control for guest users
    allComments = getAccessibleComments(allComments);
    
    // Filter resolved comments based on showResolved state
    if (!showResolved) {
      allComments = allComments.filter(comment => !comment.isResolved);
    }
    
    // Group comments by date and sort
    const groupedComments = allComments.reduce((groups: any, comment: CommentType) => {
      const date = new Date(comment.createdAt).toLocaleDateString('tr-TR');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(comment);
      return groups;
    }, {});
    
    // Sort comments within each group by createdAt
    Object.keys(groupedComments).forEach(date => {
      groupedComments[date].sort((a: CommentType, b: CommentType) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    });
    
    return groupedComments;
  };



  // Handle mention click for comment list input
  const handleMentionClickList = () => {
    setIsMentionDropdownVisibleList(!isMentionDropdownVisibleList);
    setMentionSearchTermList('');
    setSelectedMentionIndex(0);
  };

  // Handle mention select for comment list input
  const handleCommentListMentionSelect = (user: any) => {
    const displayText = `@${user.firstName}`;
    
    const lastAtIndex = commentListInput.lastIndexOf('@');
    const beforeAt = commentListInput.substring(0, lastAtIndex);
    const newComment = beforeAt + displayText + ' ';
    
    setCommentListInput(newComment);
    
    // Add to mention mapping
    setMentionMapping(prev => ({
      ...prev,
      [displayText]: user.email
    }));
    
    setIsMentionDropdownVisibleList(false);
    setMentionSearchTermList('');
    setIsMentionDisabledList(false);
  };

  // Handle comment input change
  const handleCommentListChange = (value: string) => {
    setCommentListInput(value);

    const currentLastAtIndex = value.lastIndexOf('@');
    const previousLastAtIndex = commentListInput.lastIndexOf('@');

    // Re-enable mention if new @ is typed while disabled
    if (isMentionDisabledList && currentLastAtIndex > previousLastAtIndex) {
      setIsMentionDisabledList(false);
      const afterAt = value.substring(currentLastAtIndex + 1);
      setMentionSearchTermList(afterAt);
      setIsMentionDropdownVisibleList(true);
      setSelectedMentionIndex(0);
      return;
    }

    // Skip if mention is disabled
    if (isMentionDisabledList) {
      return;
    }

    // Check for @ symbol
    if (currentLastAtIndex !== -1) {
      const afterAt = value.substring(currentLastAtIndex + 1);
      if (!afterAt.includes(' ')) {
        setMentionSearchTermList(afterAt);
        setIsMentionDropdownVisibleList(true);
        setSelectedMentionIndex(0);
      } else {
        setIsMentionDropdownVisibleList(false);
      }
    } else {
      setIsMentionDropdownVisibleList(false);
    }
  };

  // Get filtered mention users for comment list input
  const filteredMentionUsersList = fileDetail?.share?.filter((user: any) => {
    if (!mentionSearchTermList) return true;
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    const search = mentionSearchTermList.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  }) || [];

  // Handle comment send
  const handleCommentSend = async () => {
    if (!fileDetail?.id) {
      alert('File information not found. Please refresh the page.');
      return;
    }
    
    // Check if there's text or drawing
    if (!commentListInput.trim() && shapes.length === 0) {
      return;
    }

    let startFrame = 1;
    let endFrame = Math.round(videoDuration * FPS);

    // Use frame selection if available
    if (currentFrameRange) {
      const frameParts = currentFrameRange.split('-');
      if (frameParts.length === 2) {
        startFrame = parseInt(frameParts[0]);
        endFrame = parseInt(frameParts[1]);
      } else {
        startFrame = parseInt(frameParts[0]);
        endFrame = startFrame;
      }
    } else if (selectedFrame !== null) {
      startFrame = selectedFrame;
      endFrame = selectedFrame;
    } else if (selectedFrameRange !== null) {
      startFrame = selectedFrameRange[0];
      endFrame = selectedFrameRange[1];
    } else {
      // Use current frame if no selection
      const currentFrame = Math.round(currentVideoTime * FPS);
      startFrame = currentFrame;
      endFrame = currentFrame;
    }

    // Validate and correct frame values
    if (startFrame > endFrame) {
      [startFrame, endFrame] = [endFrame, startFrame];
    }

    if (endFrame < startFrame) endFrame = startFrame;

    if (startFrame > endFrame) endFrame = startFrame;

    const data = {
      videoTime: currentVideoTime,
      fps: currentFps,
      shapes: shapes || []
    };

    // Process mentions
    let processedText = commentListInput.trim() || '-';
    
    Object.keys(mentionMapping).forEach(mentionText => {
      const email = mentionMapping[mentionText];
      processedText = processedText.replace(mentionText, `{{${email}}}`);
    });

    // Determine target version ID based on active player in compare mode
    let targetVersionId = selectedVersion;
    if (compareMode && activePlayer === 2 && selectedVersionCompare) {
      targetVersionId = selectedVersionCompare;
    }

    const commentData = {
      versionId: targetVersionId,
      startFrame,
      endFrame,
      text: processedText,
      data: JSON.stringify(data),
    };

    // Open guest modal for non-logged users
    if (!user) {
      setPendingCommentData(commentData);
      setIsGuestCommentModalOpen(true);
      return;
    }

    try {
      const response = await createFileComment(fileDetail.id, commentData);
      if (response.success) {
        // Reset all states after successful comment
        setCommentListInput('');
        setMentionMapping({});
        setIsMentionDropdownVisibleList(false);
        setMentionSearchTermList('');
        setIsMentionDisabledList(false);
        
        // Reset drawing states
        onShapesChange([]);
        onShapesCompareChange([]);
        onCloseDrawingPopover(true);
        
        // Reset frame selection
        onResetFrameSelection();
        
        // Refresh file detail to get updated comments
        onFileDetailRefresh();
      } else {
        alert(`Comment could not be sent: ${response.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      alert('An error occurred while sending the comment.');
    }
  };

  // Handle guest comment submit
  const handleGuestCommentSubmit = async (guestData: { firstName: string; lastName: string; email: string }) => {
    if (!pendingCommentData || !fileDetail?.id) {
      alert('Comment data not found.');
      return;
    }

    setGuestCommentLoading(true);
    try {
      let processedText = pendingCommentData.text || '-';
      
      Object.keys(mentionMapping).forEach(mentionText => {
        const email = mentionMapping[mentionText];
        processedText = processedText.replace(mentionText, `{{${email}}}`);
      });

      const response = await createGuestFileComment(fileDetail.id, {
        ...pendingCommentData,
        text: processedText,
        ...guestData
      });

      if (response.success) {
        // Reset all states after successful guest comment
        setCommentListInput('');
        setMentionMapping({});
        setIsMentionDropdownVisibleList(false);
        setMentionSearchTermList('');
        setIsMentionDisabledList(false);
        
        // Reset drawing states
        onShapesChange([]);
        onShapesCompareChange([]);
        setIsGuestCommentModalOpen(false);
        setPendingCommentData(null);
        
        // Close drawing toolboxes and popovers
        onCloseDrawingPopover(true);
        
        // Reset frame selection
        onResetFrameSelection();
        
        // Guest kullanıcıyı session'a ekle
        setUser({
          id: 0,
          firstName: guestData.firstName,
          lastName: guestData.lastName,
          email: guestData.email,
          role: 'Guest',
          title: 'Guest User',
          organizationName: null,
          isActive: true,
          createdAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString()
        });
        
        // Refresh file detail to get updated comments
        onFileDetailRefresh();
      } else {
        alert(`Comment could not be sent: ${response.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      alert('An error occurred while sending the comment.');
    } finally {
      setGuestCommentLoading(false);
    }
  };

  // Export comments to Excel
  const handleExportComments = () => {
    if (!fileDetail?.versions) {
      message.error('No comments to export');
      return;
    }

    let allComments: CommentType[] = [];
    
    if (showAllComments) {
      fileDetail.versions.forEach((version: any) => {
        if (version.comments && Array.isArray(version.comments)) {
          const versionComments = version.comments.map((comment: any) => ({
            ...comment,
            versionId: version.id,
            versionName: version.name
          }));
          allComments = [...allComments, ...versionComments];
        }
      });
    } else {
      const selectedVersionData = fileDetail.versions.find((v: any) => v.id === selectedVersion);
      if (selectedVersionData?.comments && Array.isArray(selectedVersionData.comments)) {
        allComments = selectedVersionData.comments.map((comment: any) => ({
          ...comment,
          versionId: selectedVersionData.id,
          versionName: selectedVersionData.name
        }));
      }
    }

    allComments = getAccessibleComments(allComments);

    // Filter resolved comments based on showResolved state
    if (!showResolved) {
      allComments = allComments.filter(comment => !comment.isResolved);
    }

    if (allComments.length === 0) {
      message.error('No comments to export');
      return;
    }

    // Prepare data for Excel
    const excelData = allComments.map((comment: CommentType) => ({
      'Date': new Date(comment.createdAt).toLocaleDateString('tr-TR'),
      'Time': new Date(comment.createdAt).toLocaleTimeString('tr-TR'),
      'User': comment.user ? `${comment.user.firstName} ${comment.user.lastName}` : `User ${comment.userId}`,
      'Role': comment.user?.role || 'Guest',
      'Email': comment.user?.email || '',
      'Version': comment.versionName || `v${comment.versionId}`,
      ...(fileType !== 'image' && {
        'Frame Range': comment.startFrame === comment.endFrame 
          ? `${comment.endFrame}` 
          : `${comment.startFrame}-${comment.endFrame}`
      }),
      'Comment': comment.text || '(No comment)',
      'Resolved': comment.isResolved ? 'Yes' : 'No',
      'Created At': new Date(comment.createdAt).toLocaleString('tr-TR'),
      'Updated At': comment.updatedAt ? new Date(comment.updatedAt).toLocaleString('tr-TR') : ''
    }));

    // Convert to XLSX format
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Comments');
    
    // Download file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileDetail.name}_comments_${new Date().toISOString().split('T')[0]}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success('Comments exported successfully!');
  };

  const groupedComments = getAllComments();

  // Get file type for drawing toolbox visibility
  const getFileType = (fileName: string, videoUrl?: string) => {
    let extension: string | undefined = undefined;
    
    if (fileName && fileName.includes('.')) {
      const parts = fileName.split('.');
      if (parts.length > 1) {
        extension = parts.pop()?.toLowerCase();
      }
    }
    
    if ((!extension || extension === fileName.toLowerCase()) && videoUrl) {
      const cleanUrl = videoUrl.split('?')[0].split('#')[0];
      const urlExtension = cleanUrl.split('.').pop()?.toLowerCase().trim();
      if (urlExtension && urlExtension.length <= 4) {
        const cleanExtension = urlExtension.replace(/[^a-z0-9]/gi, '');
        if (cleanExtension) {
          extension = cleanExtension;
        }
      }
    }
    
    let result = 'other';
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) {
      result = 'video';
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'ico'].includes(extension || '')) {
      result = 'image';
    } else if (['pdf'].includes(extension || '')) {
      result = 'pdf';
    } else if (['doc', 'docx'].includes(extension || '')) {
      result = 'document';
    }
    
    return result;
  };

  const fileUrl = fileDetail?.versions?.[0]?.fileUrl || 
                 fileDetail?.url || 
                 fileDetail?.fileUrl || 
                 fileDetail?.downloadUrl || 
                 fileDetail?.path || '';
  const fileType = getFileType(fileDetail?.name || '', fileUrl);
  const isVideoFile = fileType === 'video';
  const isImageFile = fileType === 'image';
  const showDrawingToolbox = isVideoFile || isImageFile;

  return (
    <>
      {!compareMode && (
        <div className={styles.versionSelectContainer}>
          <VersionManager
            fileDetail={fileDetail}
            versions={versions}
            versionsLoading={versionsLoading}
            selectedVersion={selectedVersion}
            compareMode={compareMode}
            onVersionChange={onVersionChange}
          />
          {canComment && userRole !== 'BrandAccountViewer' && (
            <Button type="primary" onClick={onAddNewFile}>
              + Add Version
            </Button>
          )}
        </div>
      )}
      
      {/* Show resolved checkbox - always visible */}
      <div className={styles.showResolvedContainer}>
        <Checkbox
          checked={showResolved}
          onChange={(e) => setShowResolved(e.target.checked)}
          className={styles.showResolvedCheckbox}
        >
          Show resolved
        </Checkbox>
      </div>
      
      {/* Show all comments checkbox - only for compare mode with multiple versions */}
      {versions.length > 1 && canShowAllComments && compareMode && (
        <div className={styles.showAllCommentsContainer}>
          <Checkbox
            checked={showAllComments}
            onChange={(e) => onShowAllCommentsChange(e.target.checked)}
            className={styles.showAllCommentsCheckbox}
          >
            Show all comments
          </Checkbox>
        </div>
      )}

      <div className={styles.commentListContainer}>
        <div className={styles.commentListContent}>
          {/* Render grouped comments */}
          {(() => {
            // Get comments for active player
            const selectedVersionDataCompare = accessibleVersions.find((v: any) => v.id === selectedVersionCompare);
            let commentsCompare: CommentType[] = [];
            
            if (showAllComments) {
              // Compare modunda ise sadece compare edilen versiyonların yorumlarını göster
              if (compareMode) {
                // Compare edilen iki versiyonun yorumlarını göster
                const compareVersions = [selectedVersion, selectedVersionCompare].filter(Boolean);
                accessibleVersions.forEach((version: any) => {
                  if (compareVersions.includes(version.id) && version.comments && Array.isArray(version.comments)) {
                    const versionComments = version.comments.map((comment: any) => ({
                      ...comment,
                      versionId: version.id,
                      versionName: version.name || `v${version.id}`
                    }));
                    commentsCompare = [...commentsCompare, ...versionComments];
                  }
                });
              } else {
                // Normal modda tüm versiyonların yorumlarını göster
                accessibleVersions.forEach((version: any) => {
                  if (version.comments && Array.isArray(version.comments)) {
                    const versionComments = version.comments.map((comment: any) => ({
                      ...comment,
                      versionId: version.id,
                      versionName: version.name || `v${version.id}`
                    }));
                    commentsCompare = [...commentsCompare, ...versionComments];
                  }
                });
              }
            } else {
              // showAllComments false ise aktif player'daki versiyonun yorumlarını göster
              if (compareMode) {
                // Compare modunda aktif player'a göre versiyon seç
                let targetVersion: number;
                if (activePlayer === 1) {
                  targetVersion = selectedVersion;
                } else {
                  targetVersion = selectedVersionCompare || selectedVersion;
                }
                
                const selectedVersionData = accessibleVersions.find((v: any) => v.id === targetVersion);
                if (selectedVersionData?.comments && Array.isArray(selectedVersionData.comments)) {
                  commentsCompare = selectedVersionData.comments.map((comment: any) => ({
                    ...comment,
                    versionId: selectedVersionData.id,
                    versionName: selectedVersionData.name || `v${selectedVersionData.id}`
                  }));
                }
              } else {
                // Normal modda seçili versiyonun yorumlarını göster
                const selectedVersionData = accessibleVersions.find((v: any) => v.id === selectedVersion);
                if (selectedVersionData?.comments && Array.isArray(selectedVersionData.comments)) {
                  commentsCompare = selectedVersionData.comments.map((comment: any) => ({
                    ...comment,
                    versionId: selectedVersionData.id,
                    versionName: selectedVersionData.name || `v${selectedVersionData.id}`
                  }));
                }
              }
            }

            const currentComments = activePlayer === 1 ? 
              Object.values(groupedComments).flat() as CommentType[] : 
              commentsCompare;
            
            // Group comments by date for display
            const groupedCommentsForDisplay = currentComments.reduce((groups: any, comment: CommentType) => {
              const date = new Date(comment.createdAt).toLocaleDateString('tr-TR');
              if (!groups[date]) {
                groups[date] = [];
              }
              groups[date].push(comment);
              return groups;
            }, {} as Record<string, CommentType[]>);
            
            // Sort comments within each group
            Object.keys(groupedCommentsForDisplay).forEach(date => {
              groupedCommentsForDisplay[date].sort((a: CommentType, b: CommentType) => 
                new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
              );
            });

            return Object.entries(groupedCommentsForDisplay).map(([date, dateComments]: [string, any], groupIndex: number) => {
              return (
                <div key={date}>
                  {/* Date separator */}
                  {groupIndex > 0 && (
                    <div className={styles.commentDateSeparator} />
                  )}
                  
                  <div className={styles.commentDateGroup}>
                    {/* Date header */}
                    <div className={styles.commentDateHeader}>
                      {date}
                    </div>
                    
                    {/* Date underline */}
                    <div className={styles.commentDateUnderline} />
                  
                    {/* Comments for this date */}
                    {dateComments.map((item: CommentType, idx: number) => {
                      return (
                        <div
                          key={`${activePlayer}-${item.id || idx}`}
                          id={`comment-${activePlayer}-${item.id}`}
                          className={styles.commentItem}
                        >
                          {/* Comment Card */}
                          <div 
                            className={styles.commentCard}
                            style={{
                              position: 'relative', // Relative pozisyon ekledik
                              border: (() => {
                                // Compare modunda hangi player'da seçili olduğunu kontrol et
                                if (compareMode) {
                                  if (item.versionId === selectedVersion && selectedCommentId === item.id) {
                                    return '1px solid var(--app-accent-color)'; // Player 1'de seçili
                                  } else if (item.versionId === selectedVersionCompare && selectedCommentId === item.id) {
                                    return '1px solid var(--app-accent-color)'; // Player 2'de seçili
                                  }
                                  return undefined;
                                } else {
                                  // Normal modda aktif player'a göre kontrol et
                                  if (activePlayer === 1 && selectedCommentId === item.id) {
                                    return '1px solid var(--app-accent-color)';
                                  } else if (activePlayer === 2 && selectedCommentId === item.id) {
                                    return '1px solid var(--app-accent-color)';
                                  }
                                  return undefined;
                                }
                              })(),
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              // Compare modunda ise, yorumun versiyonunun compare'da olup olmadığını kontrol et
                              if (compareMode) {
                                const isVersionInCompare = [selectedVersion, selectedVersionCompare].includes(item.versionId);
                                if (!isVersionInCompare) {
                                  message.warning('Bu versiyon compare modunda mevcut değil. Compare modunu kapatarak bu versiyona gidebilirsiniz.');
                                  return;
                                }
                                
                                // Yorumun versiyonuna göre doğru player'ı seç ve seçili yorumu güncelle
                                if (item.versionId === selectedVersion) {
                                  onCommentSelect(item);
                                  // Diğer player'daki çizimi temizle
                                  onShapesCompareChange([]);
                                } else if (item.versionId === selectedVersionCompare) {
                                  onCommentSelectCompare(item);
                                  // Diğer player'daki çizimi temizle
                                  onShapesChange([]);
                                }
                              } else {
                                // Normal modda aktif player'a göre seç
                                if (activePlayer === 1) {
                                  onCommentSelect(item);
                                } else {
                                  onCommentSelectCompare(item);
                                }
                              }
                            }}
                          >
                            {/* Header: Avatar + User name + Version + Frame */}
                            <div className={styles.commentHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {/* Avatar */}
                                <div className={styles.commentAvatar} style={{
                                  backgroundColor: item.user?.thumbnailUrl ? 'transparent' : (item.user?.role === 'Guest' ? '#FFC107' : undefined),
                                  backgroundImage: item.user?.thumbnailUrl ? 'none' : undefined
                                }}>
                                  {item.user?.thumbnailUrl ? (
                                    <img
                                      src={item.user.thumbnailUrl}
                                      alt={`${item.user?.firstName || ''} ${item.user?.lastName || ''}`}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                                    />
                                  ) : (
                                    (() => {
                                    const firstName = item.user?.firstName || '';
                                    const lastName = item.user?.lastName || '';
                                    const firstLetter = firstName.charAt(0).toUpperCase();
                                    const lastLetter = lastName.charAt(0).toUpperCase();
                                    return firstLetter + lastLetter;
                                    })()
                                  )}
                                </div>
                                
                                <span className={styles.commentUserName} style={{
                                  color: item.user?.role === 'Guest' ? '#FFC107' : undefined
                                }}>
                                  {item.user?.firstName && item.user?.lastName 
                                    ? `${item.user.firstName} ${item.user.lastName}`
                                    : `User ${item.userId}`
                                  }
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                                {showAllComments && (
                                  <span
                                    className={styles.commentVersionLabel}
                                    style={{
                                      fontSize: '12px',
                                      color: '#888',
                                      backgroundColor: '#333',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      cursor: 'pointer'
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent comment selection
                                      const version = versions.find(v => v.id === item.versionId);
                                      if (version) {
                                        // Compare modunda ise, yorumun versiyonunun compare'da olup olmadığını kontrol et
                                        if (compareMode) {
                                          const isVersionInCompare = [selectedVersion, selectedVersionCompare].includes(item.versionId);
                                          if (!isVersionInCompare) {
                                            message.warning('Bu versiyon compare modunda mevcut değil. Compare modunu kapatarak bu versiyona gidebilirsiniz.');
                                            return;
                                          }
                                        }
                                        
                                        onVersionChange(item.versionId.toString());
                                        onShowAllCommentsChange(false);
                                        if (version.name) {
                                          window.location.hash = version.name;
                                        } else if (version.path) {
                                          const pathParts = version.path.split('/');
                                          const versionName = pathParts[pathParts.length - 1];
                                          if (versionName) {
                                            window.location.hash = versionName;
                                          }
                                        }
                                      }
                                    }}
                                    title={`Go to ${item.versionName || `Version ${item.versionId}`}`}
                                  >
                                    {item.versionName || `v${item.versionId}`}
                                  </span>
                                )}
                                {fileType !== 'image' && (
                                  <span
                                    className={styles.commentFrameLabel}
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent comment selection
                                      // Compare modunda ise, yorumun versiyonunun compare'da olup olmadığını kontrol et
                                      if (compareMode) {
                                        const isVersionInCompare = [selectedVersion, selectedVersionCompare].includes(item.versionId);
                                        if (!isVersionInCompare) {
                                          message.warning('Bu versiyon compare modunda mevcut değil. Compare modunu kapatarak bu versiyona gidebilirsiniz.');
                                          return;
                                        }
                                        
                                        // Yorumun versiyonuna göre doğru player'ı seç ve seçili yorumu güncelle
                                        if (item.versionId === selectedVersion) {
                                          onCommentSelect(item);
                                          // Diğer player'daki çizimi temizle
                                          onShapesCompareChange([]);
                                        } else if (item.versionId === selectedVersionCompare) {
                                          onCommentSelectCompare(item);
                                          // Diğer player'daki çizimi temizle
                                          onShapesChange([]);
                                        }
                                      } else {
                                        // Normal modda aktif player'a göre seç
                                        if (activePlayer === 1) {
                                          onCommentSelect(item);
                                        } else {
                                          onCommentSelectCompare(item);
                                        }
                                      }
                                    }}
                                    title="Go to this frame"
                                  >
                                    {item.startFrame === item.endFrame
                                      ? `${item.endFrame}`
                                      : `${item.startFrame}-${item.endFrame}`}
                                  </span>
                                )}
                              </div>
                            </div>
                              
                            {/* Message */}
                            <div className={styles.commentMessage}>
                              {(() => {
                                const text = item.text || '';
                                if (!text) return <i className={styles.commentNoMessage}>(No comment)</i>;
                                
                                // Process {{email}} format for mentions
                                const emailRegex = /\{\{([^}]+)\}\}/g;
                                const parts = text.split(emailRegex);
                                const matches = text.match(emailRegex);
                                
                                if (!matches) return text;
                                
                                const result: (string | JSX.Element)[] = [];
                                let matchIndex = 0;
                                
                                parts.forEach((part, index) => {
                                  if (index % 2 === 0) {
                                    if (part) result.push(part);
                                  } else {
                                    const email = matches[matchIndex].replace(/\{\{|\}\}/g, '');
                                    const user = fileDetail?.share?.find((u: any) => u.email === email);
                                    
                                    if (user) {
                                      const displayName = `@${user.firstName}`;
                                      result.push(
                                        <span key={`mention-${matchIndex}`} style={{ color: 'var(--app-accent-color)', fontWeight: '600' }}>
                                          {displayName}
                                        </span>
                                      );
                                    } else {
                                      result.push(
                                        <span key={`mention-${matchIndex}`} style={{ color: 'var(--app-accent-color)', fontWeight: '600' }}>
                                          {email}
                                        </span>
                                      );
                                    }
                                    matchIndex++;
                                  }
                                });
                                
                                return result;
                              })()}
                            </div>
                            
                            {/* Resolve + Edit/Delete Area */}
                            {(canResolve || canEditDeleteComment(item)) && (
                              <div 
                                className={styles.commentResolveArea}
                                style={{
                                  position: 'absolute',
                                  bottom: '8px',
                                  left: '12px',
                                  right: '12px',
                                  zIndex: 10,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {canResolve && (
                                    <>
                                <input
                                  type="checkbox"
                                  id={`resolve-${activePlayer}-${item.id}`}
                                  style={{ 
                                    display: 'none',
                                    accentColor: 'var(--app-accent-color)'
                                  }}
                                  checked={!!item.isResolved}
                                  readOnly
                                />
                                <span
                                  onClick={async (e) => {
                                          e.stopPropagation();
                                    const newValue = !item.isResolved;
                                    setResolvingComments(prev => ({ ...prev, [item.id]: true }));
                                    try {
                                      const res = await resolveFileComment(item.id, newValue);
                                      if (res && res.success && fileDetail?.id) {
                                        setTimeout(async () => {
                                          const refreshRes = await getFileById(fileDetail.id);
                                          if (refreshRes.success && refreshRes.data) {
                                            onFileDetailRefresh();
                                          }
                                          setResolvingComments(prev => ({ ...prev, [item.id]: false }));
                                              }, 800);
                                      } else {
                                        setResolvingComments(prev => ({ ...prev, [item.id]: false }));
                                      }
                                    } catch (error) {
                                      setResolvingComments(prev => ({ ...prev, [item.id]: false }));
                                    }
                                  }}
                                  className={`${styles.commentResolveCheckbox} ${item.isResolved ? styles.resolved : ''} ${resolvingComments[item.id] ? styles.resolving : ''}`}
                                >
                                  {item.isResolved && !resolvingComments[item.id] && (
                                    <svg width={10} height={10} viewBox="0 0 12 12">
                                      <polyline
                                        points="2,7 5,10 10,3"
                                        style={{ fill: 'none', stroke: '#fff', strokeWidth: 2 }}
                                      />
                                    </svg>
                                  )}
                                  {resolvingComments[item.id] && (
                                    <div className={styles.resolveLoadingSpinner} />
                                  )}
                                </span>
                                <label
                                  htmlFor={`resolve-${activePlayer}-${item.id}`}
                                  className={`${styles.commentResolveLabel} ${item.isResolved ? styles.resolved : ''} ${resolvingComments[item.id] ? styles.resolving : ''}`}
                                  onClick={async (e) => {
                                          e.stopPropagation();
                                    const newValue = !item.isResolved;
                                    setResolvingComments(prev => ({ ...prev, [item.id]: true }));
                                    try {
                                      const res = await resolveFileComment(item.id, newValue);
                                      if (res && res.success && fileDetail?.id) {
                                        setTimeout(async () => {
                                          const refreshRes = await getFileById(fileDetail.id);
                                          if (refreshRes.success && refreshRes.data) {
                                            onFileDetailRefresh();
                                          }
                                          setResolvingComments(prev => ({ ...prev, [item.id]: false }));
                                              }, 800);
                                      } else {
                                        setResolvingComments(prev => ({ ...prev, [item.id]: false }));
                                      }
                                    } catch (error) {
                                      setResolvingComments(prev => ({ ...prev, [item.id]: false }));
                                    }
                                  }}
                                >
                                  {resolvingComments[item.id] ? 'Resolving...' : (item.isResolved ? 'Resolved' : 'Resolve')}
                                </label>
                                    </>
                                  )}
                                </div>
                                {canEditDeleteComment(item) && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<EditOutlined />}
                                      style={{ color: 'var(--app-accent-color)' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditComment(item);
                                      }}
                                      title="Edit comment"
                                    />
                                    <Button
                                      type="text"
                                      size="small"
                                      style={{ color: 'var(--app-accent-color)' }}
                                      icon={<DeleteOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteComment(item.id);
                                      }}
                                      title="Delete comment"
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {/* Comment input area */}
        <div className={styles.commentListInputContainer}>
          {/* Frame info - absolute positioned */}
          {fileType !== 'image' && (compareMode ? (activePlayer === 1 ? currentFrameRange : currentFrameRangeCompare) : currentFrameRange) && (
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '12px',
              background: 'rgba(var(--app-accent-rgb), 0.9)',
              color: '#fff',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              zIndex: 1,
              pointerEvents: 'none'
            }}>
              {compareMode ? (activePlayer === 1 ? currentFrameRange : currentFrameRangeCompare) : currentFrameRange}
            </div>
          )}
          
          {/* Input area */}
          <div className={styles.commentInputArea}>
            <Input.TextArea
              className={styles.commentInputNoBorder}
              placeholder="Start typing..."
              value={commentListInput}
              onChange={e => handleCommentListChange(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
              onKeyDown={(e) => {
                if (isMentionDropdownVisibleList && filteredMentionUsersList.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedMentionIndex((prev) => (prev + 1) % filteredMentionUsersList.length);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedMentionIndex((prev) => (prev - 1 + filteredMentionUsersList.length) % filteredMentionUsersList.length);
                  } else if (e.key === 'Escape') {
                    setIsMentionDropdownVisibleList(false);
                  }
                }
              }}
              onPressEnter={e => { 
                if (!e.shiftKey) { 
                  e.preventDefault(); 
                  if (isMentionDropdownVisibleList && filteredMentionUsersList.length > 0) {
                    const user = filteredMentionUsersList[selectedMentionIndex];
                    if (user) {
                      handleCommentListMentionSelect(user);
                    }
                  } else {
                  handleCommentSend(); 
                  }
                } 
              }}
            />
          </div>
          
          {/* Button area */}
          <div className={styles.commentButtonArea}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <Button
                icon={<DownloadOutlined />}
                className={styles.commentExportBtn}
                onClick={() => handleExportComments()}
                title="Export to Excel"
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ position: 'relative' }}>
                <Button
                  icon={<span>@</span>}
                  className={styles.commentMentionBtnNoBorder}
                  onClick={handleMentionClickList}
                />
              </div>
              
              {showDrawingToolbox && (
                <div className={styles.drawingToolboxMobile}>
                  <CompactDrawingToolbox
                    tool={drawingTool}
                    color={drawingColor}
                    onToolChange={onToolChange}
                    onColorChange={onColorChange}
                    onClear={onClearShapes}
                    onSave={onSave}
                    onDeleteSelected={activePlayer === 1 ? onDeleteSelected : onDeleteSelectedCompare}
                    onChangeSelectedColor={activePlayer === 1 ? onChangeSelectedColor : onChangeSelectedColorCompare}
                    onUndo={activePlayer === 1 ? onUndo : onUndoCompare}
                    onRedo={activePlayer === 1 ? onRedo : onRedoCompare}
                    hasSelectedShape={activePlayer === 1 ? !!selectedShapeId : !!selectedShapeIdCompare}
                    canUndo={activePlayer === 1 ? canUndo : canUndoCompare}
                    canRedo={activePlayer === 1 ? canRedo : canRedoCompare}
                    selectedShapeColor={activePlayer === 1 
                      ? (selectedShapeId ? (shapes.find(s => s.id === selectedShapeId)?.color) : undefined)
                      : (selectedShapeIdCompare ? (shapesCompare.find(s => s.id === selectedShapeIdCompare)?.color) : undefined)
                    }
                    onClose={closeDrawingPopover ? () => onCloseDrawingPopover(false) : undefined}
                    onOpenChange={onDrawingToolboxOpenChange}
                  />
                </div>
              )}
              
              <Button
                type="primary"
                shape="circle"
                icon={<SendOutlined />}
                onClick={handleCommentSend}
                className={styles.commentSendBtnNoBorder}
                disabled={!commentListInput.trim() && shapes.length === 0}
              />
            </div>
          </div>
          
          {/* Mention dropdown */}
          {isMentionDropdownVisibleList && (
            <div className={styles.mentionDropdown}>
              <div className={styles.mentionDropdownHeader}>
                <Input
                  placeholder="Search users..."
                  value={mentionSearchTermList}
                  onChange={(e) => setMentionSearchTermList(e.target.value)}
                  size="small"
                  className={styles.mentionDropdownSearch}
                />
              </div>
              <div className={styles.mentionDropdownList}>
                {filteredMentionUsersList.length > 0 ? (
                  filteredMentionUsersList.map((user: any, idx: number) => (
                    <div
                      key={user.id}
                      onClick={() => handleCommentListMentionSelect(user)}
                      className={styles.mentionDropdownItem}
                      style={{ backgroundColor: idx === selectedMentionIndex ? '#3a3a3a' : undefined }}
                      onMouseEnter={() => setSelectedMentionIndex(idx)}
                    >
                      <div className={styles.mentionDropdownAvatar}>
                        {user.firstName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className={styles.mentionDropdownUserName}>
                          {user.firstName} {user.lastName}
                          {user.role === 'Guest' && <span className={styles.guestLabel}> (guest)</span>}
                        </div>
                        <div className={styles.mentionDropdownUserEmail}>
                          {user.email}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.mentionDropdownEmpty}>
                    No users found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Guest Comment Modal */}
      <GuestCommentModal
        isOpen={isGuestCommentModalOpen}
        onClose={() => setIsGuestCommentModalOpen(false)}
        onSubmit={handleGuestCommentSubmit}
        loading={guestCommentLoading}
      />

      {/* Edit Modal */}
      <Modal
        title="Edit Comment"
        open={isEditModalOpen}
        onOk={handleSaveEdit}
        onCancel={handleCancelEdit}
        footer={[
          <Button key="cancel" onClick={handleCancelEdit}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={editCommentLoading} onClick={handleSaveEdit} style={{ background: 'var(--app-accent-color)', borderColor: 'var(--app-accent-color)' }}>
            Save
          </Button>,
        ]}
      >
        <Input.TextArea
          value={editCommentText}
          onChange={(e) => setEditCommentText(e.target.value)}
          autoSize={{ minRows: 1, maxRows: 3 }}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              handleSaveEdit();
            }
          }}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        title="Delete Comment"
        open={isDeleteModalOpen}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        footer={[
          <Button key="cancel" onClick={handleCancelDelete}>
            Cancel
          </Button>,
          <Button key="delete" type="primary" loading={deleteCommentLoading} onClick={handleConfirmDelete} style={{ background: 'var(--app-accent-color)', borderColor: 'var(--app-accent-color)' }}>
            Delete
          </Button>,
        ]}
      >
        <p>Are you sure you want to delete this comment?</p>
      </Modal>
    </>
  );
};

export default CommentSection; 